import React, { useEffect, useRef, useState } from 'react'
import { Streamdown, type Components } from 'streamdown'
import { createCodePlugin } from '@streamdown/code'
import { createCjkPlugin } from '@streamdown/cjk'
import { Icon } from '@bosch/react-frok'
import cn from 'classnames'

import "streamdown/styles.css";
import './CustomMarkdown.scss'

// Created once at module level so Shiki's lazy-loading cache is never reset.
const code = createCodePlugin({ themes: ['github-light', 'github-dark'] })
const cjk = createCjkPlugin()

// Streamdown expects SVG-compatible components; Bosch Icon renders as <i> so
// we drop the forwarded SVG props and render the Bosch icon directly.
const BoschCopyIcon = () => <Icon iconName="copy" />
const BoschCheckIcon = () => <Icon iconName="checkmark" />

interface CustomMarkdownProps {
    className?: string | null
    children?: string
    // Optional — defaults to false (static rendering, no animation)
    isStreaming?: boolean
}

const ALink = ({ ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a target="_blank" rel="noreferrer" {...props} />
)

const CustomMarkdown = ({
    className,
    children,
    isStreaming = false,
}: CustomMarkdownProps) => {
    const [isAnimating, setIsAnimating] = useState(false)
    const stopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (isStreaming) {
            if (stopTimerRef.current !== null) {
                clearTimeout(stopTimerRef.current)
                stopTimerRef.current = null
            }
            setIsAnimating(true)
        } else {
            stopTimerRef.current = setTimeout(() => {
                setIsAnimating(false)
                stopTimerRef.current = null
            }, 100)
        }

        return () => {
            if (stopTimerRef.current !== null) clearTimeout(stopTimerRef.current)
        }
    }, [isStreaming])

    return (
        <div className={cn('custom-markdown', className)}>
            <Streamdown
                animated
                isAnimating={isAnimating}
                plugins={{ code, cjk }}
                controls={{ code: { download: false } }}
                lineNumbers={false}
                components={{ a: ALink as Components['a'] }}
                icons={{ CopyIcon: BoschCopyIcon, CheckIcon: BoschCheckIcon }}
            >
                {(children || '')}
            </Streamdown>
        </div>
    )
}

export default CustomMarkdown
